import React from 'react';
import { IconType } from '@src/types/IconType';
import { ButtonType } from '@src/types/types';
import { classNames } from '@utils/classNames';
import { LoadingSpinner } from '@components/atoms/LoadingSpinner';
import { Icon } from '@components/atoms/Icon';

export type ButtonProps = {
  label?: string | React.ReactElement;
  IconLeading?: IconType;
  IconTrailing?: IconType;
  buttonType: ButtonType;
  disabled?: boolean;
  loading?: boolean;
  iconClassName?: string;
  testingId?: string;
  alignContentStart?: boolean;
  size?: 'default' | 'small';
} & React.ButtonHTMLAttributes<HTMLButtonElement>;

const focusClasses = '';
const enabledClasses = '';

const types: Record<ButtonType, { main: string; icons: string }> = {
  [ButtonType.PRIMARY]: {
    main:
      'bg-ivarioYellow text-dark ' +
      'hover:bg-ivarioYellow-hover ' +
      'disabled:bg-grey2 ' +
      'disabled:hover:!bg-grey2 ' +
      focusClasses +
      enabledClasses,
    icons: 'text-dark h-3.5 w-3.5',
  },
  [ButtonType.SECONDARY]: {
    main:
      'bg-dark text-white ' +
      'hover:bg-dark-hover ' +
      'disabled:bg-grey2 ' +
      'disabled:hover:!bg-grey2 ' +
      enabledClasses +
      ' group',
    icons: 'text-white h-3.5 w-3.5',
  },
  [ButtonType.TERTIARY]: {
    main:
      'bg-ivarioBlue text-white ' +
      'hover:bg-ivarioBlue-hover ' +
      'disabled:bg-grey2 ' +
      'disabled:hover:!bg-grey2 ' +
      enabledClasses +
      ' group',
    icons: 'text-white h-3.5 w-3.5',
  },
  [ButtonType.WHITE]: {
    main:
      'text-dark outline outline-1 outline-dark bg-background ' +
      'hover:bg-grey2 ' +
      'disabled:bg-grey2 ' +
      'disabled:hover:!bg-grey2 ' +
      focusClasses +
      enabledClasses,
    icons: 'text-dark h-3.5 w-3.5',
  },
  [ButtonType.CLOSE]: {
    main: 'h-2 w-2!',
    icons: 'text-dark h-3.5 w-3.5',
  },
};

export function Button({
  label,
  IconLeading,
  IconTrailing,
  buttonType,
  disabled = false,
  loading = false,
  iconClassName = '',
  testingId,
  alignContentStart = false,
  size = 'default',
  type = 'button',
  ...rest
}: ButtonProps) {
  return (
    <>
      <button
        {...rest}
        type={type}
        className={classNames(
          'inline-flex text-sm relative items-center gap-x-1.5 rounded-bonusLarge font-bold',
          !loading && 'enabled:active:translate-y-0.5',
          'disabled:cursor-not-allowed',
          loading && 'cursor-default',
          types[buttonType].main,
          size === 'default' ? 'py-2.5' : 'py-1.5',
          size === 'default' ? 'w-[300px]' : 'w-fit',
          rest.className,
        )}
        disabled={disabled}
        onClick={disabled || loading ? undefined : rest.onClick}
        {...(testingId ? { 'data-testid': testingId } : {})}
      >
        {loading && (
          <LoadingSpinner
            className={classNames(
              'absolute inset-x-0 inset-y-auto',
              size === 'default' ? 'h-5' : 'h-4',
            )}
          />
        )}
        {
          <div
            className={classNames(
              'w-full inline-flex relative items-center gap-x-1.5 font-bold',
              size === 'default' ? 'text-sm' : 'text-xs',
              alignContentStart ? 'justify-start' : 'justify-center',
              loading && 'invisible',
            )}
          >
            {loading ? (
              <LoadingSpinner
                className={
                  size === 'default' ? 'h-3 w-3 py-2.5' : 'h-2.5 w-2.5 py-2'
                }
              />
            ) : (
              <>
                {IconLeading && (
                  <Icon
                    type={IconLeading}
                    className={classNames(
                      'shrink-0',
                      types[buttonType].icons,
                      size === 'default' ? 'h-3.5 w-3.5' : 'h-3 w-3',
                      iconClassName,
                    )}
                  />
                )}
                {label}
                {IconTrailing && (
                  <Icon
                    type={IconTrailing}
                    className={classNames(
                      'shrink-0',
                      types[buttonType].icons,
                      size === 'default' ? 'h-3.5 w-3.5' : 'h-3 w-3',
                      iconClassName,
                    )}
                  />
                )}
              </>
            )}
          </div>
        }
      </button>
    </>
  );
}
